import { useEffect } from 'react';

import { Card, Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import {
  CheckCircleFillIcon,
  CrossCircleIcon,
  InformationCircleIcon,
  SquareArrowRightUpIcon,
} from '@grupoboticario/flora-react-icons';

import { ProgressBar, ResponsiveFloraTypography } from 'components';
import { getFormattedPrice, pluralize } from 'helpers';
import { useIncentiveCampaign } from 'hooks';
import {
  getChallenges,
  getInvalidMemberFields,
  isInvalidChallengeMember,
} from 'pages/IncentiveCampaign/helpers';
import { useLoyaltyIncentiveCampaign } from 'pages/IncentiveCampaign/hooks';

import Styles from './ChallengesResults.styles';

export const ChallengesResults = () => {
  const { accumulated, sso } = useIncentiveCampaign;
  const { campaignId } = useLoyaltyIncentiveCampaign();
  const { data, isLoading } = accumulated.useQuery(campaignId);
  const {
    mutate: ssoMutate,
    data: ssoData,
    isLoading: ssoLoading,
    isSuccess: ssoSuccess,
    isError: ssoError,
  } = sso.useMutation();
  const isInvalidMember = isInvalidChallengeMember(data?.validRegistrationFields);
  const invalidFields = getInvalidMemberFields(data?.validRegistrationFields);

  const navigateToProfile = () => {
    window.open('/perfil', '_blank', 'noopener, noreferrer');
  };

  const handleSSOClick = () => {
    ssoMutate({});
  };

  useEffect(() => {
    if (ssoError) {
      Toaster.notify({
        kind: 'error',
        description:
          'Não foi possível acessar a página de resgate de prêmios. Por favor, tente novamente.',
        origin: 'right-bottom',
      });
      return;
    }
    if (ssoSuccess && ssoData?.success && ssoData.ssoUrl) {
      window.open(ssoData.ssoUrl, '_blank', 'noopener, noreferrer');
    }
  }, [ssoError, ssoSuccess, ssoData]);

  const points = data?.points ?? 0;
  const formattedPoints = new Intl.NumberFormat('pt-BR').format(points);

  if (isLoading || data == null) return <></>;
  const levelChallenges = getChallenges(data.challenges);
  const currentChallenge = levelChallenges.current;
  const nextChallenge = levelChallenges.next;

  return (
    <Flex direction='column' gap='$8'>
      <Flex direction='column' gap='$6'>
        <ResponsiveFloraTypography fontSize='title' fontWeight='bold' as='h2'>
          Compre e ganhe
        </ResponsiveFloraTypography>

        <Flex direction='column' gap='$4'>
          <ResponsiveFloraTypography color='$nonInteractiveAuxiliar'>
            Durante o período da campanha, suas compras valiam pontos que, agora ao final, podem ser
            trocados por prêmios no catálogo do incentivo, desde que você tenha alcançado pelo menos
            o primeiro desafio e com status adimplentes na data de apuração 08/01/2024. <br />
            <br />
            Confira abaixo como foi a sua performance na campanha e, lembre-se, caso tenha pontos
            elegíveis, você terá de 27/01/2025 a 31/03/2025 para resgatá-los.
          </ResponsiveFloraTypography>
        </Flex>

        <Styles.ChallengeWrapper>
          <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold' as='h6'>
            Confira seu resultado
          </ResponsiveFloraTypography>
          <Styles.CardsWrapper size='medium'>
            <Styles.ResellerProgress>
              <ResponsiveFloraTypography fontWeight='bold'>
                Acumulado no período da campanha.
              </ResponsiveFloraTypography>

              <Card>
                <Flex direction='column' gap='$4'>
                  <Flex justify={currentChallenge != null ? 'space-between' : 'end'} gap='$4'>
                    <Flex direction='column'>
                      <ResponsiveFloraTypography
                        fontSize='bodyLargeShort'
                        color='$nonInteractiveAuxiliar'
                      >
                        {currentChallenge != null ? 'Nível atingido' : 'Próximo nível'}
                      </ResponsiveFloraTypography>
                      <ResponsiveFloraTypography fontWeight='medium' fontSize='bodyLargeShort'>
                        {currentChallenge != null ? currentChallenge?.name : nextChallenge?.name}
                      </ResponsiveFloraTypography>
                    </Flex>
                  </Flex>
                  <Flex direction='column' gap='$2'>
                    <ProgressBar
                      maxValue={nextChallenge?.amount ?? data.amount}
                      size='medium'
                      value={data.amount ?? 0}
                    />

                    <Flex justify='start' align='center' gap='$1'>
                      <InformationCircleIcon size={14} />
                      <ResponsiveFloraTypography
                        fontSize='bodyLargeShort'
                        color='$nonInteractiveAuxiliar'
                      >
                        Total acumulado no período
                      </ResponsiveFloraTypography>
                      <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                        {getFormattedPrice(data.amount)}
                      </ResponsiveFloraTypography>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
              <ResponsiveFloraTypography fontWeight='bold'>
                {currentChallenge == null ? 'Desafio da campanha' : 'Desafio atingido'}
              </ResponsiveFloraTypography>
              <Styles.Challenges size='medium'>
                <Styles.ChallengesCardWrapper>
                  <Styles.ChallengesCard data-complete={currentChallenge != null}>
                    <Flex gap='$1'>
                      {currentChallenge != null ? (
                        <CheckCircleFillIcon color='$success-standard' size={20} />
                      ) : (
                        <CrossCircleIcon color='$error-standard' size={20} />
                      )}
                      <Flex direction='column' gap='$4'>
                        <Flex direction='column' align='start'>
                          <ResponsiveFloraTypography
                            fontSize='exceptionsAuxiliar'
                            color='$nonInteractiveAuxiliar'
                          >
                            {currentChallenge != null
                              ? `${currentChallenge?.name} - CONCLUÍDO`
                              : `${nextChallenge?.name} - NÃO ATINGIDO`}
                          </ResponsiveFloraTypography>

                          <ResponsiveFloraTypography
                            fontSize='bodyLargeShort'
                            color='$nonInteractiveAuxiliar'
                            fontWeight='medium'
                          >
                            A partir de{' '}
                            {getFormattedPrice(
                              currentChallenge != null
                                ? currentChallenge?.amount
                                : nextChallenge?.amount ?? 0
                            )}{' '}
                            reais
                          </ResponsiveFloraTypography>
                        </Flex>

                        <ResponsiveFloraTypography fontWeight='bold'>
                          R$ 1,00 ={' '}
                          {new Intl.NumberFormat('pt', { minimumFractionDigits: 1 }).format(
                            currentChallenge?.multiplier ?? 1
                          )}{' '}
                          {pluralize(currentChallenge?.multiplier ?? 1, 'ponto', `pontos`)}
                        </ResponsiveFloraTypography>
                      </Flex>
                    </Flex>
                  </Styles.ChallengesCard>
                </Styles.ChallengesCardWrapper>
              </Styles.Challenges>
            </Styles.ResellerProgress>

            <Styles.ResellerProgress>
              <ResponsiveFloraTypography fontWeight='bold'>
                Pontos disponíveis para o resgate
              </ResponsiveFloraTypography>

              <Card>
                <Flex direction='column' gap='$4'>
                  <ResponsiveFloraTypography
                    fontSize='subtitle'
                    fontWeight='bold'
                    as='h1'
                    color='$nonInteractiveEmphasis'
                  >
                    {formattedPoints} {pluralize(points, 'ponto', `pontos`)}
                  </ResponsiveFloraTypography>
                  {points === 0 ? (
                    <></>
                  ) : (
                    <Flex direction='column' gap='$2'>
                      <FloraButton
                        isLoading={ssoLoading}
                        disabled={ssoLoading || isInvalidMember}
                        onClick={handleSSOClick}
                        autotagging-flora='on'
                        data-flora='button'
                        icon={<SquareArrowRightUpIcon size={16} />}
                        has='iconLeft'
                        data-flora-text='resgatar-premios'
                      >
                        <ResponsiveFloraTypography fontWeight='bold' fontSize='bodyLargeShort'>
                          Resgatar prêmios
                        </ResponsiveFloraTypography>
                      </FloraButton>
                      <Styles.LinkCard>
                        <Styles.CardInfo>
                          {isInvalidMember && (
                            <Styles.Wrapper>
                              <Flex direction='row'>
                                <InformationCircleIcon size={28} color='$nonInteractiveAuxiliar' />

                                <Styles.Message>
                                  <ResponsiveFloraTypography
                                    color='$nonInteractivePredominant'
                                    fontWeight='bold'
                                  >
                                    Antes de resgatar, acesse o seu perfil e atualize os seus dados:
                                  </ResponsiveFloraTypography>
                                  <Styles.Items>
                                    {invalidFields.map((field, index) => (
                                      <ResponsiveFloraTypography
                                        key={index}
                                        fontSize='bodyLargeShort'
                                        fontWeight='bold'
                                        color='$nonInteractivePredominant'
                                      >
                                        • {field}
                                      </ResponsiveFloraTypography>
                                    ))}
                                  </Styles.Items>

                                  <ResponsiveFloraTypography
                                    fontSize='bodyLargeShort'
                                    color='$nonInteractivePredominant'
                                  >
                                    Você tem até 31/03/2025 para resgatar seus pontos.
                                  </ResponsiveFloraTypography>
                                  <ResponsiveFloraTypography
                                    fontSize='bodyLargeShort'
                                    color='$nonInteractivePredominant'
                                  >
                                    Após atualizar, aguarde até 48 horas para que seu acesso ao
                                    resgate esteja disponível.
                                  </ResponsiveFloraTypography>
                                </Styles.Message>
                              </Flex>
                              <FloraButton
                                onClick={navigateToProfile}
                                autotagging-flora='on'
                                data-flora='button'
                                icon={<SquareArrowRightUpIcon size={16} />}
                                has={'iconLeft'}
                                data-flora-text='acessar-perfil'
                              >
                                <ResponsiveFloraTypography fontWeight='bold'>
                                  Acessar perfil
                                </ResponsiveFloraTypography>
                              </FloraButton>
                            </Styles.Wrapper>
                          )}
                        </Styles.CardInfo>
                      </Styles.LinkCard>
                    </Flex>
                  )}
                </Flex>
              </Card>
            </Styles.ResellerProgress>
          </Styles.CardsWrapper>
        </Styles.ChallengeWrapper>
      </Flex>
    </Flex>
  );
};
